import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Bell, Loader, CheckCircle, AlertCircle, Send, Users, ChevronDown, ChevronRight, FileText, Plus, Pencil, Trash2, RotateCcw } from 'lucide-react';

// Settings → Messages & Alerts: compose an SMS and broadcast it to drivers via the
// gated send-sms function (one call per recipient, paced), then record the batch in
// the sms_log settings KV. Sender is the registered "HPR Engine" alphanumeric ID:
// one-way (drivers can't reply) and emojis are blocked (carriers filter them).

const EMOJI_RE = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}]/u;
const PLACEHOLDER_RE = /\{(\w+)\}/g;
// {name} auto-resolves per recipient at send time; every other {xxx} token is a
// blank the admin fills in once (same value goes to every recipient this send).
const PERSONAL_PLACEHOLDERS = new Set(['name']);

function extractPlaceholders(text) {
  const found = new Set();
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(text))) found.add(m[1]);
  return [...found];
}

function resolvePlaceholders(text, recipient, fillValues) {
  return text.replace(PLACEHOLDER_RE, (full, key) => {
    if (key === 'name') return (recipient && (recipient.name || recipient.login)) || full;
    return (fillValues && fillValues[key] !== undefined && fillValues[key] !== '') ? fillValues[key] : full;
  });
}

function segmentInfo(text) {
  const ucs2 = /[^\x20-\x7E\n\r]/.test(text); // non-basic-ASCII forces UCS-2 encoding
  const per = ucs2 ? 70 : 160;
  const perMulti = ucs2 ? 67 : 153;
  const segments = text.length === 0 ? 0 : (text.length <= per ? 1 : Math.ceil(text.length / perMulti));
  return { segments, per: segments > 1 ? perMulti : per };
}

export default function MessagesAlertsPanel({ addNotification }) {
  const [drivers, setDrivers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [message, setMessage]   = useState('');
  const [mode, setMode]         = useState('active');       // active | route | custom
  const [routes, setRoutes]     = useState({});             // route name → checked
  const [picks, setPicks]       = useState({});             // login → checked
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending]   = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [lastResult, setLastResult] = useState(null);
  const [log, setLog]           = useState([]);
  const [openLog, setOpenLog]   = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplEdit, setTplEdit]   = useState(null);            // {id?, name, body, system}
  const [tplBusy, setTplBusy]   = useState(false);
  const [fillValues, setFillValues] = useState({});          // placeholder → value, e.g. {task: '...'}

  const callTemplates = useCallback(async (payload) => {
    const r = await fetch('/.netlify/functions/sms-templates-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return r.json();
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const d = await callTemplates({ action: 'loadTemplates' });
      if (d.success) setTemplates(d.templates || []);
    } catch { /* templates are non-critical */ }
  }, [callTemplates]);

  const saveTemplate = async () => {
    if (!tplEdit || !tplEdit.body.trim()) return;
    if (!tplEdit.system && !tplEdit.name.trim()) return;
    setTplBusy(true);
    try {
      const d = await callTemplates({ action: 'saveTemplate', id: tplEdit.id, name: tplEdit.name.trim(), body: tplEdit.body.trim() });
      if (d.success) { setTplEdit(null); loadTemplates(); }
      else if (addNotification) addNotification(d.error || 'Template save failed', 'error');
    } catch (e) { if (addNotification) addNotification(e.message, 'error'); }
    setTplBusy(false);
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(t.system ? `Reset "${t.name}" to the default wording?` : `Delete template "${t.name}"?`)) return;
    try {
      const d = await callTemplates({ action: 'deleteTemplate', id: t.id });
      if (d.success) loadTemplates();
      else if (addNotification) addNotification(d.error || 'Delete failed', 'error');
    } catch (e) { if (addNotification) addNotification(e.message, 'error'); }
  };

  const loadLog = useCallback(async () => {
    try {
      const r = await fetch('/.netlify/functions/sms-log-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'loadSmsLog' }),
      });
      const d = await r.json();
      if (d.success) setLog(d.log || []);
    } catch { /* history is non-critical */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/.netlify/functions/roster-drivers');
        const d = await r.json();
        if (Array.isArray(d)) setDrivers(d);
      } catch { /* handled by empty state below */ }
      setLoading(false);
    })();
    loadLog();
    loadTemplates();
  }, [loadLog, loadTemplates]);

  const allRoutes = useMemo(() => {
    const set = new Set();
    drivers.forEach(d => (d.route || '').split(/[,/]/).forEach(r => { const t = r.trim(); if (t) set.add(t); }));
    return [...set].sort();
  }, [drivers]);

  const recipients = useMemo(() => {
    const inRoute = d => (d.route || '').split(/[,/]/).some(r => routes[r.trim()]);
    let list;
    if (mode === 'active')      list = drivers.filter(d => d.active);
    else if (mode === 'route')  list = drivers.filter(d => d.active && inRoute(d));
    else                        list = drivers.filter(d => picks[d.login]);
    return list.filter(d => d.phone);
  }, [drivers, mode, routes, picks]);

  const skippedNoPhone = useMemo(() => {
    if (mode !== 'custom') return [];
    return drivers.filter(d => picks[d.login] && !d.phone).map(d => d.login);
  }, [drivers, mode, picks]);

  const hasEmoji = EMOJI_RE.test(message);
  const otherPlaceholders = useMemo(
    () => extractPlaceholders(message).filter(p => !PERSONAL_PLACEHOLDERS.has(p)),
    [message]
  );
  const missingFills = otherPlaceholders.filter(p => !(fillValues[p] || '').trim());
  const previewRecipient = recipients[0];
  const previewText = previewRecipient ? resolvePlaceholders(message, previewRecipient, fillValues) : message;
  const { segments, per } = segmentInfo(previewText);
  const canSend = message.trim().length > 0 && recipients.length > 0 && !hasEmoji && missingFills.length === 0 && !sending;

  const doSend = async () => {
    setConfirming(false);
    setSending(true);
    setLastResult(null);
    setProgress({ done: 0, total: recipients.length });
    const results = [];
    for (const d of recipients) {
      const personalized = resolvePlaceholders(message.trim(), d, fillValues);
      try {
        const r = await fetch('/.netlify/functions/send-sms', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: d.phone, message: personalized }),
        });
        const resp = await r.json();
        results.push({ login: d.login, phone: d.phone, ok: resp.ok === true, error: resp.error });
      } catch (err) {
        results.push({ login: d.login, phone: d.phone, ok: false, error: err.message });
      }
      setProgress(p => ({ ...p, done: p.done + 1 }));
      await new Promise(res => setTimeout(res, 250)); // pace the carrier
    }
    const sent = results.filter(r => r.ok).length;
    const failed = results.filter(r => !r.ok);
    try {
      await fetch('/.netlify/functions/sms-log-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'appendSmsLog', entry: { message: message.trim(), results } }),
      });
    } catch { /* log failure shouldn't mask send outcome */ }
    setSending(false);
    setLastResult({ sent, failed });
    if (failed.length === 0) { setMessage(''); setFillValues({}); }
    if (addNotification) addNotification(failed.length ? `SMS: ${sent} sent, ${failed.length} failed` : `SMS sent to ${sent} driver${sent === 1 ? '' : 's'}`, failed.length ? 'error' : 'success');
    loadLog();
  };

  const box = { padding: '16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' };
  const dim = { fontSize: '0.78rem', color: 'var(--hp-dim)' };
  const modeBtn = active => ({
    padding: '6px 14px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
    border: `1px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
    background: active ? 'var(--hp-gold)' : 'transparent',
    color: active ? 'var(--hp-black)' : 'var(--hp-text)',
  });

  if (loading) return <div style={{ padding: '32px', textAlign: 'center' }}><Loader size={18} className="spin" /> Loading drivers…</div>;

  return (
    <>
      <h4 style={{ marginBottom: '4px' }}>Messages &amp; Alerts</h4>
      <p style={{ ...dim, marginBottom: '24px' }}>
        Send an SMS from <strong>HPR Engine</strong> to your drivers. One-way — drivers can't reply. No emojis (carriers filter them).
      </p>

      {/* Compose */}
      <div style={{ ...box, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Message</div>
          <button className="btn" style={{ fontSize: '0.75rem', padding: '3px 9px' }}
            onClick={() => setMessage(m => m + (m && !/\s$/.test(m) ? ' ' : '') + '{name}')}>
            + Insert {'{name}'}
          </button>
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          placeholder="Hi {name}, …"
          style={{ width: '100%', resize: 'vertical', background: 'var(--hp-black)', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px', fontSize: '0.85rem', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
          <span style={dim}>{previewText.length} chars · {segments} SMS segment{segments === 1 ? '' : 's'} ({per}/segment)</span>
          {hasEmoji && <span style={{ ...dim, color: 'var(--hp-red, #e05555)' }}><AlertCircle size={12} style={{ verticalAlign: '-2px' }} /> Remove emojis — carriers block them</span>}
        </div>

        {message.includes('{name}') && (
          <div style={{ ...dim, marginTop: '8px' }}>
            <CheckCircle size={12} style={{ verticalAlign: '-2px', color: 'var(--hp-green)' }} /> {'{name}'} will be replaced with each driver's own name automatically.
          </div>
        )}

        {otherPlaceholders.length > 0 && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--hp-border)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '8px' }}>Fill in the blanks</div>
            {otherPlaceholders.map(p => (
              <div key={p} style={{ marginBottom: '8px' }}>
                <label style={{ ...dim, display: 'block', marginBottom: '4px' }}>{`{${p}}`}</label>
                <input
                  value={fillValues[p] || ''}
                  onChange={e => setFillValues(o => ({ ...o, [p]: e.target.value }))}
                  placeholder={`Value for {${p}}`}
                  style={{ width: '100%', background: 'var(--hp-black)', color: 'var(--hp-text)', border: `1px solid ${!(fillValues[p] || '').trim() ? 'var(--hp-red, #e05555)' : 'var(--hp-border)'}`, borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: '0.85rem' }}
                />
              </div>
            ))}
            <div style={dim}>Same value is used for every recipient in this send.</div>
          </div>
        )}

        {previewRecipient && (message.includes('{name}') || otherPlaceholders.length > 0) && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--hp-border)' }}>
            <div style={{ ...dim, marginBottom: '4px' }}>Preview — as {previewRecipient.name || previewRecipient.login} will receive it:</div>
            <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>{previewText}</div>
          </div>
        )}
      </div>

      {/* Recipients */}
      <div style={{ ...box, marginBottom: '16px' }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '10px' }}><Users size={14} style={{ verticalAlign: '-2px' }} /> Recipients</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button style={modeBtn(mode === 'active')} onClick={() => setMode('active')}>All active drivers</button>
          <button style={modeBtn(mode === 'route')}  onClick={() => setMode('route')}>By route</button>
          <button style={modeBtn(mode === 'custom')} onClick={() => setMode('custom')}>Pick drivers</button>
        </div>

        {mode === 'route' && (
          <div style={{ display: 'flex', gap: '14px', marginBottom: '10px' }}>
            {allRoutes.map(r => (
              <label key={r} style={{ fontSize: '0.83rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!routes[r]} onChange={e => setRoutes(o => ({ ...o, [r]: e.target.checked }))} /> {r}
              </label>
            ))}
          </div>
        )}

        {mode === 'custom' && (
          <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(190px, 100%), 1fr))', gap: '4px', marginBottom: '10px' }}>
            {drivers.map(d => (
              <label key={d.login} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', opacity: d.phone ? 1 : 0.45 }}>
                <input type="checkbox" checked={!!picks[d.login]} onChange={e => setPicks(o => ({ ...o, [d.login]: e.target.checked }))} />
                {d.login}{d.active ? '' : ' (inactive)'}{d.phone ? '' : ' — no mobile'}
              </label>
            ))}
          </div>
        )}

        <div style={dim}>
          {recipients.length} driver{recipients.length === 1 ? '' : 's'} will receive this message.
          {skippedNoPhone.length > 0 && <> Skipped (no mobile): {skippedNoPhone.join(', ')}.</>}
        </div>
      </div>

      {/* Send */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        {!confirming ? (
          <button className="btn btn-primary" disabled={!canSend} onClick={() => setConfirming(true)}
            style={{ opacity: canSend ? 1 : 0.5, cursor: canSend ? 'pointer' : 'not-allowed' }}>
            <Send size={14} /> Send SMS
          </button>
        ) : (
          <>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Send to {recipients.length} driver{recipients.length === 1 ? '' : 's'}?</span>
            <button className="btn btn-primary" onClick={doSend}><Send size={14} /> Yes, send now</button>
            <button className="btn" onClick={() => setConfirming(false)}>Cancel</button>
          </>
        )}
        {!confirming && !sending && missingFills.length > 0 && (
          <span style={{ ...dim, color: 'var(--hp-red, #e05555)' }}>Fill in {missingFills.map(p => `{${p}}`).join(', ')} above to send.</span>
        )}
        {sending && <span style={dim}><Loader size={13} className="spin" style={{ verticalAlign: '-2px' }} /> Sending {progress.done}/{progress.total}…</span>}
        {lastResult && !sending && (
          <span style={{ ...dim, color: lastResult.failed.length ? 'var(--hp-red, #e05555)' : 'var(--hp-green)' }}>
            {lastResult.failed.length
              ? <><AlertCircle size={13} style={{ verticalAlign: '-2px' }} /> {lastResult.sent} sent, failed: {lastResult.failed.map(f => f.login).join(', ')}</>
              : <><CheckCircle size={13} style={{ verticalAlign: '-2px' }} /> Sent to {lastResult.sent} driver{lastResult.sent === 1 ? '' : 's'}</>}
          </span>
        )}
      </div>

      {/* Templates */}
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
        <FileText size={12} style={{ verticalAlign: '-2px' }} /> Templates
      </div>
      <p style={{ ...dim, marginBottom: '12px' }}>
        Saved messages you can reuse. The <strong>auto</strong> ones are the wording of the scheduled reminders — edit them here
        and the next automated send uses your version. If you <strong>Use</strong> one here to send manually, {'{name}'} fills in
        automatically per driver and any other {'{placeholder}'} appears under "Fill in the blanks" above. Reset restores the default wording.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
        {templates.map(t => (
          <div key={t.id} style={{ ...box, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{t.name}</span>
              {t.system && <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)', borderRadius: '10px', padding: '1px 8px' }}>AUTO</span>}
              {t.system && t.custom && <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>(customised)</span>}
              <span style={{ flex: 1 }} />
              <button className="btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => setMessage(t.body)}>Use</button>
              <button className="btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => setTplEdit({ id: t.id, name: t.name, body: t.body, system: t.system, placeholders: t.placeholders })}><Pencil size={12} /> Edit</button>
              {(t.custom || !t.system) && (
                <button className="btn" style={{ fontSize: '0.75rem', padding: '4px 10px' }} onClick={() => deleteTemplate(t)}>
                  {t.system ? <><RotateCcw size={12} /> Reset</> : <><Trash2 size={12} /> Delete</>}
                </button>
              )}
            </div>
            <div style={{ ...dim, marginTop: '6px', whiteSpace: 'pre-wrap' }}>{t.body}</div>
          </div>
        ))}
      </div>

      {tplEdit ? (
        <div style={{ ...box, marginBottom: '24px' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '10px' }}>{tplEdit.id ? `Edit — ${tplEdit.name}` : 'New template'}</div>
          {!tplEdit.system && (
            <input
              value={tplEdit.name}
              onChange={e => setTplEdit(o => ({ ...o, name: e.target.value }))}
              placeholder="Template name"
              style={{ width: '100%', marginBottom: '8px', background: 'var(--hp-black)', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: '0.85rem' }}
            />
          )}
          <textarea
            value={tplEdit.body}
            onChange={e => setTplEdit(o => ({ ...o, body: e.target.value }))}
            rows={3}
            style={{ width: '100%', resize: 'vertical', background: 'var(--hp-black)', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px', fontSize: '0.85rem', fontFamily: 'inherit' }}
          />
          {tplEdit.system && (tplEdit.placeholders || []).length > 0 && (
            <div style={{ ...dim, marginTop: '4px' }}>
              Available placeholders: {(tplEdit.placeholders || []).map(p => `{${p}}`).join(', ')} — keep them in the text so the real values are filled in.
            </div>
          )}
          {EMOJI_RE.test(tplEdit.body) && <div style={{ ...dim, color: 'var(--hp-red, #e05555)', marginTop: '4px' }}>Remove emojis — carriers block them.</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button className="btn btn-primary" disabled={tplBusy || EMOJI_RE.test(tplEdit.body) || !tplEdit.body.trim() || (!tplEdit.system && !tplEdit.name.trim())} onClick={saveTemplate}>
              {tplBusy ? 'Saving…' : 'Save template'}
            </button>
            <button className="btn" onClick={() => setTplEdit(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" style={{ marginBottom: '24px' }} onClick={() => setTplEdit({ id: '', name: '', body: '', system: false, placeholders: [] })}>
          <Plus size={14} /> New template
        </button>
      )}

      {/* History */}
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
        <Bell size={12} style={{ verticalAlign: '-2px' }} /> Send History
      </div>
      {log.length === 0 && <div style={dim}>No messages sent yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {log.map((e, i) => {
          const sent = e.results.filter(r => r.ok).length;
          const failed = e.results.length - sent;
          const open = openLog === i;
          return (
            <div key={e.ts + i} style={{ ...box, padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => setOpenLog(open ? null : i)}>
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span style={{ ...dim, whiteSpace: 'nowrap' }}>{new Date(e.ts).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                <span style={{ fontSize: '0.82rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.message}</span>
                <span style={{ ...dim, whiteSpace: 'nowrap', color: failed ? 'var(--hp-red, #e05555)' : 'var(--hp-green)' }}>
                  {sent} sent{failed ? `, ${failed} failed` : ''}
                </span>
              </div>
              {open && (
                <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
                  <div style={{ fontSize: '0.83rem', whiteSpace: 'pre-wrap', marginBottom: '8px' }}>{e.message}</div>
                  <div style={dim}>By {e.by} · {e.results.map(r => `${r.login}${r.ok ? '' : ' ✕'}`).join(', ')}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
